import React from 'react';
import Badge, { Status } from '@ids-ts/badge';
import { ThemeableElement, useTheme } from '@design-systems/theme';
import {
  CircleExclamationFill,
  CircleCheckFill,
  CircleInfoFill
} from '@design-systems/icons';
import { B3 } from '@ids-ts/typography';
import makeClass from 'clsx';
import styles from './InlineValidationMessage.css';

export type InlineValidationType =
  | 'error'
  | 'warning'
  | 'success'
  | 'invalid'
  | '';

export interface InlineValidationMessageProps
  extends Omit<ThemeableElement<'span'>, 'onChange' | 'onBlur'> {
  /**
   * AutomationID property
   *
   * @default idsInlineValidationMessage
   */
  automationId?: string;
  /** ID property */
  id?: string;
  /** message property */
  message?: string;
  /** description property */
  description?: string;
  /**
   * Determines the type of Validation Message : ['error', 'warning', 'success', 'invalid']
   *
   * @default ''
   */
  type?: InlineValidationType;
}

/**
 * A component to display an error or warning.
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const InlineValidationMessage = ({
  automationId = 'idsInlineValidationMessage',
  className,
  colorScheme,
  description,
  id,
  message,
  theme,
  type = ''
}: InlineValidationMessageProps) => {
  const { themeClass } = useTheme({ theme, colorScheme }, styles);

  let statusClass;
  let role;
  let badgeStatus: Status = 'error';
  let badgeIcon: React.ReactNode;

  if (type === 'error') {
    role = 'alert';
    badgeIcon = <CircleExclamationFill />;

    if (message !== '') {
      statusClass = styles.ivmError;
      badgeStatus = 'error';
    }
  } else if (type === 'warning') {
    if (message !== '') {
      statusClass = styles.ivmWarning;
      badgeStatus = 'warning';
    }

    badgeIcon = <CircleExclamationFill />;
  } else if (type === 'success') {
    if (message !== '') {
      statusClass = styles.ivmSuccess;
      badgeStatus = 'success';
    }

    badgeIcon = <CircleCheckFill />;
  } else if (type === 'invalid') {
    if (message !== '') {
      statusClass = styles.ivmInvalid;
      badgeStatus = 'pending';
    }

    badgeIcon = <CircleInfoFill />;
  }

  return message && type ? (
    <div
      className={makeClass(styles.ivmWrapper, themeClass, className)}
      role={role}
      id={id}
      data-automation-id={`${automationId}-alertMsg`}
    >
      <Badge
        shape="round"
        status={badgeStatus}
        className={styles.ivmStatusIcon}
      >
        {badgeIcon}
      </Badge>
      <div className={styles.ivmMessageContainer}>
        <B3 className={makeClass(styles.ivmMessage, statusClass)}>{message}</B3>
        <B3 className={styles.ivmDescription}>{description}</B3>
      </div>
    </div>
  ) : null;
};

export default InlineValidationMessage;
